/**
 * Ticket-triage agent — the third producer that does *real* work, not simulation.
 *
 * The `ticket-worker` template used to *narrate* believable progress ("swept the
 * queue and advanced in-flight work") without touching a single ticket. This
 * module makes it honest: it reads the *real* ticket queue, finds the overdue
 * work that has stalled before it ever started, and proposes a *real*
 * `set_ticket_status` write — moving the ticket into active progress — into the
 * approvals inbox.
 *
 * It performs no write itself — it only proposes. The operator approves in the
 * Dashboard approvals inbox, and `resolveApproval` runs the action through
 * `runActionWithUndo`, which genuinely flips the ticket's status (appending a
 * timeline event) and is undoable. So the loop is honest end-to-end:
 *
 *   real data  →  real proposal  →  operator approval  →  real store write
 *
 * Domains are split on purpose so deployed agents never double up: the outreach
 * monitor owns follow-ups (`schedule_follow_up`), the system scanner owns revenue
 * (`create_task`), and this owns the ticket queue (`set_ticket_status`). No
 * network, no API key — the same db adapter the humans use.
 *
 * The honesty line: it only proposes the one transition it can defend from the
 * data — escalating an *overdue but not-yet-started* ticket into `in-progress`.
 * It deliberately leaves `blocked` tickets to humans (it can't know the blocker
 * cleared) and never fabricates completion (it won't push `review → done`).
 */

import { queueApproval, listApprovals } from './approvals';
import type { Ticket, TicketStatus } from '../db/types';
import type { SetTicketStatusAction } from './types';

const DAY = 24 * 60 * 60 * 1000;

/**
 * Statuses that mean "this ticket has not started yet". An overdue ticket in
 * any of these is a candidate for escalation into active work. `in-progress`,
 * `review`, `blocked`, `done`, and `cancelled` are intentionally excluded:
 * those are either already moving, awaiting a human, or terminal.
 */
const NOT_STARTED: ReadonlySet<TicketStatus> = new Set<TicketStatus>([
  'backlog',
  'open',
  'claimed',
]);

/** Whole days a plain (YYYY-MM-DD) deadline is past `now`, on day boundaries. */
function daysOverdue(deadline: string, now: number): number {
  const due = Date.parse(deadline);
  if (Number.isNaN(due)) return 0;
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  return Math.floor((startOfToday.getTime() - due) / DAY);
}

export interface ProposeTicketTriageResult {
  /** How many new status proposals were queued for approval. */
  proposed: number;
  /** Overdue-not-started tickets that already had a pending proposal (skipped). */
  skipped: number;
}

/**
 * True when a ticket is overdue and has not started yet — the one case this
 * producer will escalate. Reads the ticket's status + deadline directly (the
 * system scanner's "Overdue ticket" finding doesn't carry status, and it also
 * flags overdue in-progress / review / blocked tickets we must leave alone, so
 * operating on the raw ticket is both simpler and more precise here).
 */
function isOverdueNotStarted(t: Ticket, now: number): boolean {
  if (!NOT_STARTED.has(t.status)) return false;
  if (!t.deadline) return false;
  return daysOverdue(t.deadline, now) > 0;
}

/** Human-readable note for the approval card. */
function noteFor(t: Ticket, now: number): string {
  const od = daysOverdue(t.deadline!, now);
  const owner = t.owner ? `owned by ${t.owner}` : 'unowned';
  return `Overdue ${od} day${od === 1 ? '' : 's'} · ${t.priority.toUpperCase()} · ${t.status} · ${owner} — escalate into active work`;
}

/**
 * Scan the live ticket queue and queue a `set_ticket_status` approval (→
 * `in-progress`) for each overdue, not-yet-started ticket. De-dupes by ticketId
 * against pending status proposals so re-running the agent doesn't pile
 * duplicates for the same ticket. Returns how many were proposed vs. skipped.
 */
export async function proposeTicketTriage(
  tickets: Ticket[],
  now = Date.now(),
): Promise<ProposeTicketTriageResult> {
  const candidates = tickets.filter((t) => isOverdueNotStarted(t, now));

  // Don't propose a second status change for a ticket that already has one
  // waiting in the inbox.
  const pending = await listApprovals('pending');
  const alreadyQueued = new Set(
    pending
      .filter((a) => a.action.kind === 'set_ticket_status')
      .map((a) => (a.action as SetTicketStatusAction).ticketId),
  );

  let proposed = 0;
  let skipped = 0;
  for (const t of candidates) {
    if (alreadyQueued.has(t.id)) {
      skipped++;
      continue;
    }
    const action: SetTicketStatusAction = {
      kind: 'set_ticket_status',
      ticketId: t.id,
      ticketTitle: `${t.id} · ${t.title}`,
      status: 'in-progress',
    };
    await queueApproval(action, 'scheduler', noteFor(t, now));
    // Guard against the same ticket being proposed twice within one run.
    alreadyQueued.add(t.id);
    proposed++;
  }

  return { proposed, skipped };
}
